import { idempotencyConfig } from "utils/idempotency";
import { ApiResponse } from "api/HttpClient";
import { RefabricProURL } from "api/index";
import {
  IMiroAuthorizeRequest,
  IMiroAuthorizeResponse,
  IMiroBoardImagesRequest,
  IMiroBoardImagesResponse,
  IMiroBoardsRequest,
  IMiroBoardsResponse,
  IMiroCanvasExportAccepted,
  IMiroCanvasImportAccepted,
  IMiroCanvasImportRequest,
  IMiroCanvasExportRequest,
  IMiroCreateBoardRequest,
  IMiroCreatedBoard,
  IMiroCallbackRequest,
  IMiroCallbackResponse,
  IMiroDisconnectResponse,
  IMiroExportRequest,
  IMiroFramesResponse,
  IMiroImportRequest,
  IMiroJob,
  IMiroJobSummary,
  IMiroJobAccepted,
  IMiroMoodboardFromBoardAccepted,
  IMiroMoodboardFromBoardRequest,
  IMiroMoodboardJobRequest,
  IMiroOriginalImage,
  IMiroStatusResponse,
} from "./miro.type";

const BASE = "integrations/miro";

// ⚠️ `/v1/integrations/miro/*` answers Miro-side failures as CODED errors (409/422/404/429/502) —
// never 401/403. The 409s (`miro.not_connected`, `miro.reauth_required`, `miro.not_configured`)
// REJECT here because `HttpClient` path-scopes this prefix out of its generic 409 branch; callers
// read the code with `getMiroErrorCode`. No catches in this file — the caller owns the state reset.
export default {
  async status(): Promise<ApiResponse<IMiroStatusResponse>> {
    return await RefabricProURL.get(`${BASE}/status`, {});
  },

  async authorize(data: IMiroAuthorizeRequest): Promise<ApiResponse<IMiroAuthorizeResponse>> {
    return await RefabricProURL.post(`${BASE}/authorize`, data, {});
  },

  async callback(data: IMiroCallbackRequest): Promise<ApiResponse<IMiroCallbackResponse>> {
    return await RefabricProURL.post(`${BASE}/callback`, data, {});
  },

  async disconnect(): Promise<ApiResponse<IMiroDisconnectResponse>> {
    return await RefabricProURL.delete(`${BASE}/connection`, {});
  },

  async getBoards(params: IMiroBoardsRequest = {}): Promise<ApiResponse<IMiroBoardsResponse>> {
    return await RefabricProURL.get(`${BASE}/boards`, { params });
  },

  async createBoard(data: IMiroCreateBoardRequest): Promise<ApiResponse<IMiroCreatedBoard>> {
    return await RefabricProURL.post(`${BASE}/boards`, data, {});
  },

  async startCanvasExportJob(canvasId: string, data: IMiroCanvasExportRequest, idempotencyKey?: string): Promise<ApiResponse<IMiroCanvasExportAccepted>> {
    return await RefabricProURL.post(`${BASE}/canvas-projects/${encodeURIComponent(canvasId)}/export-jobs`, data, idempotencyConfig(idempotencyKey));
  },

  async startExportJob(boardId: string, data: IMiroExportRequest, idempotencyKey?: string): Promise<ApiResponse<IMiroJobAccepted>> {
    return await RefabricProURL.post(`${BASE}/boards/${encodeURIComponent(boardId)}/export-jobs`, data, idempotencyConfig(idempotencyKey));
  },

  async getBoardImages(boardId: string, params: IMiroBoardImagesRequest = {}): Promise<ApiResponse<IMiroBoardImagesResponse>> {
    return await RefabricProURL.get(`${BASE}/boards/${encodeURIComponent(boardId)}/images`, { params });
  },

  async getOriginalImage(boardId: string, itemId: string): Promise<ApiResponse<IMiroOriginalImage>> {
    return await RefabricProURL.get(`${BASE}/boards/${encodeURIComponent(boardId)}/images/${encodeURIComponent(itemId)}/original`, {});
  },

  async startCanvasImportJob(boardId: string, data: IMiroCanvasImportRequest, idempotencyKey?: string): Promise<ApiResponse<IMiroCanvasImportAccepted>> {
    return await RefabricProURL.post(`${BASE}/boards/${encodeURIComponent(boardId)}/canvas-import-jobs`, data, idempotencyConfig(idempotencyKey));
  },

  async startImportJob(boardId: string, data: IMiroImportRequest, idempotencyKey?: string): Promise<ApiResponse<IMiroJobAccepted>> {
    return await RefabricProURL.post(`${BASE}/boards/${encodeURIComponent(boardId)}/import-jobs`, data, idempotencyConfig(idempotencyKey));
  },

  async startMoodboardJob(boardId: string, data: IMiroMoodboardJobRequest, idempotencyKey?: string): Promise<ApiResponse<IMiroJobAccepted>> {
    return await RefabricProURL.post(`${BASE}/boards/${encodeURIComponent(boardId)}/moodboard-jobs`, data, idempotencyConfig(idempotencyKey));
  },

  async getBoardFrames(boardId: string): Promise<ApiResponse<IMiroFramesResponse>> {
    return await RefabricProURL.get(`${BASE}/boards/${encodeURIComponent(boardId)}/frames`, {});
  },

  async startMoodboardFromBoardJob(boardId: string, data: IMiroMoodboardFromBoardRequest, idempotencyKey?: string): Promise<ApiResponse<IMiroMoodboardFromBoardAccepted>> {
    return await RefabricProURL.post(`${BASE}/boards/${encodeURIComponent(boardId)}/moodboard-from-board-jobs`, data, idempotencyConfig(idempotencyKey));
  },

  async listJobs(params: { active?: boolean; since_minutes?: number } = {}): Promise<ApiResponse<IMiroJobSummary[] | { items: IMiroJobSummary[] }>> {
    return await RefabricProURL.get(`${BASE}/jobs`, { params });
  },

  async getJob(jobId: string): Promise<ApiResponse<IMiroJob>> {
    return await RefabricProURL.get(`${BASE}/jobs/${encodeURIComponent(jobId)}`, {});
  },
};
